import React, { useEffect, useMemo } from 'react';

import { dataLayerProvider, tracker } from '@femtasy/helpers';
import cn from 'classnames';
import { useRouteMatch } from 'react-router';

import IconFemtasy from 'components/IconFemtasy';
import Loader from 'components/Loader';
import LoveIcon from 'components/LoveIcon';
import Message from 'components/Message';
import PageContainer from 'components/PageContainer';
import Pill, { contentTypeSlugToIconType } from 'components/Pill';
import SimilarStories from 'components/SimilarStories';
import SpeakerTag from 'components/SpeakerTag';
import TagPillList from 'components/TagPillList';
import ThreeDotsMenu from 'components/ThreeDotsMenu';
import Typography from 'components/Typography';

import TopNavigation from 'containers/TopNavigation';

import {
  StoryActions,
  useGetStoryDetailsQuery,
} from 'graphql/generated/graphql';

import { isProduction } from 'helpers/env';
import { localizeWithCurrentUrl } from 'helpers/locales';

import { useAlgolia } from 'hooks/useAlgolia';
import { useAuth } from 'hooks/useAuth';
import useGraphaelDefaults from 'hooks/useGraphaelDefaults';

import { ContentType } from 'types/contentType';
import { StoryAudioType } from 'types/storyAudioType';

import ActionsMenu from './ActionsMenu';
import Buttons from './Buttons';

import './style.scss';

const onboardingPagePattern = 'onboarding/results/\\d+/details';
const shouldShowSimilarStories = (path: string) => {
  return !path.match(new RegExp(onboardingPagePattern, 'g'));
};

const audioTrackingType = (isFullAdio: boolean, isFreeTrial: boolean) => {
  if (isFullAdio && isFreeTrial) {
    return StoryAudioType.Unlocked;
  }

  if (isFullAdio && !isFreeTrial) {
    return StoryAudioType.FullAudio;
  }

  return StoryAudioType.Teaser;
};

const StoryDetails = () => {
  const { sendObjectViewedInsight } = useAlgolia();
  const { isAuthenticated } = useAuth();
  const [showActionsMenu, setShowActionsMenu] = React.useState(false);

  const {
    params: { id: storyId },
    url,
  } = useRouteMatch<{ id: string; url: string; section: string }>();

  useMemo(() => {
    sendObjectViewedInsight({
      eventName: 'story_details_opened',
      index: 'Stories',
      objectId: storyId,
    });
  }, [sendObjectViewedInsight, storyId]);

  const { data: storyDetailsData } = useGetStoryDetailsQuery({
    ...useGraphaelDefaults(),
    variables: {
      storyId,
    },
  });

  const story = storyDetailsData?.story;
  const actionMenuIconRef = React.useRef<HTMLDivElement>(null);
  const getBackLink = React.useCallback(() => {
    return url
      .replace(`/${storyId}/details`, '')
      .replace(`/${storyId}/player`, '');
  }, [storyId, url]);

  useEffect(() => {
    if (story) {
      const isFullAudio = story.actions.includes(StoryActions.Play);

      tracker.sendEvent(
        'audio',
        [
          {
            audioAction: 'view',
            audioName: story.title,
            audioUrl: isFullAudio
              ? story.transcodedAudioUrl || ''
              : story.transcodedSampleUrl || '',
            audioSpeaker: story.speaker.name,
            audioType: audioTrackingType(isFullAudio, story.freeTrial),
            pageUrl: window.location.pathname,
          },
        ],
        !isProduction(),
        [dataLayerProvider],
      );
    }
  }, [story]);

  return (
    <PageContainer
      className={cn('StoryDetails', story && 'StoryDetails--medium')}
      withTabBar={true}
    >
      <TopNavigation
        backLink={getBackLink()}
        actions={[
          story && (
            <LoveIcon
              femtasyRed={story.femtasyRed}
              itemId={story.id}
              loved={story.loved}
              playlists={story.playlists}
            />
          ),
          // key are already handled by the navigation bar action rendering
          // eslint-disable-next-line react/jsx-key
          <ThreeDotsMenu
            actionMenuIconRef={actionMenuIconRef}
            onClick={() => setShowActionsMenu(!showActionsMenu)}
          />,
        ]}
      />
      <ActionsMenu
        actionMenuIconRef={actionMenuIconRef}
        story={story}
        open={showActionsMenu}
        onClose={() => setShowActionsMenu(false)}
      />

      {story ? (
        <div className="StoryDetails__content">
          <div className="storyTitle">
            <Typography variant="h2">{story.title}</Typography>
            <div className="duration">
              <IconFemtasy icon="Clockline" size="16" />
              <Typography variant="small-thick" component="span">
                {story.durationInMinutes}
              </Typography>
            </div>
          </div>

          <div className="audio-type-details">
            <div className="SpeakerDetails">
              <Pill
                isSelected={false}
                leftIcon={contentTypeSlugToIconType(
                  story.contentType.slug as ContentType,
                )}
                message={story.contentType.name}
                linkTo={
                  isAuthenticated
                    ? localizeWithCurrentUrl(
                        `/search/results?contentTypeName=${story.contentType.slug}&title=${story.contentType.name}`,
                      )
                    : undefined
                }
              />
              <Typography variant="body" component="span">
                <Message
                  message={{
                    id: 'components.StoryDetails.audioTypeDetails.joinText',
                  }}
                />
              </Typography>
              <SpeakerTag speaker={story.speaker} />
            </div>

            <div className="languageIntensity">
              <Typography variant="body" component="span">
                <Message
                  message={{ id: 'components.StoryDetails.languageIntensity' }}
                />
                {': '}
              </Typography>
              <Pill
                isSelected={false}
                message={story.languageIntensity.name}
                linkTo={
                  isAuthenticated
                    ? localizeWithCurrentUrl(
                        `/search/results?languageIntensityName=${story.languageIntensity.slug}&title=${story.languageIntensity.name}`,
                      )
                    : undefined
                }
              />
            </div>
          </div>

          <Buttons story={story} />

          <div className="description">
            <Typography variant="body">{story.description}</Typography>
          </div>

          <div className="storyTags-container">
            <Typography variant="small-thin" component="span">
              <Message
                message={{
                  id: 'components.StoryDetails.tagsLabel',
                  values: { count: story.tags.length },
                }}
              />
            </Typography>

            <TagPillList
              className="storyTags"
              tags={story.tags}
              onTagClick={(tagId) => {
                sendObjectViewedInsight({
                  eventName: 'tag_clicked_story_details',
                  index: 'Tags',
                  objectId: tagId,
                });
              }}
            />
          </div>

          {shouldShowSimilarStories(url) && (
            <>
              <hr />
              <SimilarStories storyId={story.id} />
            </>
          )}
        </div>
      ) : (
        <div className="loader-wrapper">
          <Loader />
        </div>
      )}
    </PageContainer>
  );
};

export default StoryDetails;
